import { HttpError, OfferImportContextDTO } from '@luminovo/api-contracts';
import { getToken } from '@luminovo/auth';
import { compareByDate, isPresent } from '@luminovo/commons';
import { suspenseHttpQuery, useHttpQuery, useSuspenseHttpQuery } from '@luminovo/data-stack';
import { formatToIso8601Date } from '@luminovo/frontend-utils';
import { http } from '@luminovo/http-client';

/**
 * One import, for its page and its route's loader. Quiet on failure: the route turns a 404 into the not-found page, and
 * after a delete the page's reload fails before it leaves.
 */
export function offerImportContextQuery(id: string) {
    return suspenseHttpQuery(
        'GET /offer-import-contexts/:id',
        { pathParams: { id } },
        { meta: { globalErrorHandler: false } },
    );
}

export function useOfferImportContexts() {
    return useSuspenseHttpQuery(
        'GET /offer-import-contexts',
        {},
        {
            select: (data) => Array.from(data.items).sort(compareByDate((c) => c.created_at)),
        },
    );
}

/** Offers carry only `import_context_name`, which is unique. */
export function useOfferImportContextByName(name: string | null | undefined) {
    return useHttpQuery(
        'GET /offer-import-contexts',
        {},
        {
            enabled: isPresent(name),
            select: (data) => data.items.find((importContext) => importContext.name === name),
        },
    );
}

const OFFER_IMPORT_CONTEXT_NAME_MAX_LENGTH = 256;
const PASTED_CSV_FILE_NAME = 'pasted-csv.csv';

/** A pasted CSV gets a dated name, so no context takes the internal paste placeholder's name. */
export function resolveOfferImportContextName(fileName: string | undefined): string {
    if (!isPresent(fileName) || fileName === PASTED_CSV_FILE_NAME || fileName.trim().length === 0) {
        return `Offer upload ${formatToIso8601Date(new Date())}`;
    }
    return fileName.slice(0, OFFER_IMPORT_CONTEXT_NAME_MAX_LENGTH);
}

/** Reuses the context with the same name, e.g. when the same file is uploaded again. */
export async function getOrCreateManualOfferImportContext(name: string): Promise<OfferImportContextDTO> {
    try {
        return await http(
            'POST /offer-import-contexts',
            {
                requestBody: {
                    name,
                    origin: 'ManualFileUpload',
                },
            },
            getToken(),
        );
    } catch (error) {
        if (error instanceof HttpError && error.code === 'offer_import_context.duplicate_name') {
            const { items } = await http('GET /offer-import-contexts', {}, getToken());
            const existing = items.find((importContext) => importContext.name === name);
            if (isPresent(existing)) {
                return existing;
            }
        }
        throw error;
    }
}
